/**
 * Build a single "shareable collage" JPG for a set of event photos.
 *
 * Renders on a client-side <canvas> so it works in TWA/mobile browsers
 * without needing a backend image pipeline. The output is Instagram-Story-
 * friendly (1080×1350 portrait) and includes a header (event title +
 * attendee count) and a footer watermark.
 *
 * Returns a Blob (image/jpeg) ready to be handed to navigator.share or a
 * download <a>. Photos that fail to load are skipped silently so a broken
 * URL never blocks the whole collage.
 */

const OUT_W = 1080;
const OUT_H = 1350;
const PADDING = 32;
const HEADER_H_BASE = 200;
// Minimum header height when a caption is present (1-line captions should
// still have enough breathing room). Grows dynamically if the caption wraps.
const HEADER_H_WITH_CAPTION_BASE = 290;
const FOOTER_H = 88;

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // silent skip
    img.src = src;
  });

const wrapText = (ctx, text, x, y, maxWidth, lineHeight, maxLines = 2) => {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
    if (lines.length >= maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);
  // If we ran out of room, ellipsise the last line.
  if (lines.length === maxLines && words.length && lines[maxLines - 1] !== current) {
    lines[maxLines - 1] = lines[maxLines - 1].replace(/[.,;\s]*$/, '') + '…';
  }
  let ly = y;
  for (const l of lines) {
    ctx.fillText(l, x, ly);
    ly += lineHeight;
  }
  return lines.length * lineHeight;
};

// Dry-run wrapText: counts how many lines a string will occupy at the given
// font (ctx must already have the final font set) without drawing anything.
// Used to compute dynamic header height for the caption overlay.
const countWrappedLines = (ctx, text, maxWidth, maxLines = 3) => {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
    if (lines.length >= maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);
  return lines.length;
};

const gridLayout = (n) => {
  // Normalised layout: each entry is { x, y, w, h } in 0..1 of the photo area.
  // Picked to flatter faces: hero-first for odd small counts so no single
  // cell is forced into a wide-short shape that crops heads off.
  if (n <= 0) return { count: 0, cells: [] };
  if (n === 1) {
    return { count: 1, cells: [{ x: 0, y: 0, w: 1, h: 1 }] };
  }
  if (n === 2) {
    // Side-by-side (two near-square cells).
    return {
      count: 2,
      cells: [
        { x: 0,   y: 0, w: 0.5, h: 1 },
        { x: 0.5, y: 0, w: 0.5, h: 1 },
      ],
    };
  }
  if (n === 3) {
    // Hero on top (full width), two below — keeps near-square cells so faces
    // aren't cropped by the previous 1-col × 3-wide-row layout.
    return {
      count: 3,
      cells: [
        { x: 0,   y: 0,    w: 1,   h: 0.6 },
        { x: 0,   y: 0.6,  w: 0.5, h: 0.4 },
        { x: 0.5, y: 0.6,  w: 0.5, h: 0.4 },
      ],
    };
  }
  if (n === 4) {
    return {
      count: 4,
      cells: [
        { x: 0,   y: 0,   w: 0.5, h: 0.5 },
        { x: 0.5, y: 0,   w: 0.5, h: 0.5 },
        { x: 0,   y: 0.5, w: 0.5, h: 0.5 },
        { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
      ],
    };
  }
  if (n <= 6) {
    const cells = [];
    for (let i = 0; i < 6; i++) {
      cells.push({
        x: (i % 2) * 0.5,
        y: Math.floor(i / 2) / 3,
        w: 0.5,
        h: 1 / 3,
      });
    }
    return { count: 6, cells };
  }
  const cells = [];
  for (let i = 0; i < 9; i++) {
    cells.push({
      x: (i % 3) / 3,
      y: Math.floor(i / 3) / 3,
      w: 1 / 3,
      h: 1 / 3,
    });
  }
  return { count: 9, cells };
};

/**
 * @param {Array<{ url: string, caption?: string }>} photos
 * @param {Object} meta - { title, attendeeCount, dateLabel, caption, captionStyle, includeHeader }
 * @returns {Promise<{ blob: Blob, dataUrl: string, count: number }>}
 */
export async function buildAlbumCollage(photos, meta) {
  const canvas = document.createElement('canvas');
  canvas.width = OUT_W;
  canvas.height = OUT_H;
  const ctx = canvas.getContext('2d');

  const caption = (meta.caption || '').trim();
  const includeHeader = meta.includeHeader !== false; // default true

  // Pre-measure caption to compute a dynamic header height. We pick the
  // same font the actual draw will use so line counts match. Base header
  // (no caption) = 200px. With caption, add 60px of top gap + (lines ×
  // lineHeight) + ~30px bottom padding.
  let HEADER_H = HEADER_H_BASE;
  if (includeHeader && caption) {
    const style = meta.captionStyle || 'quote';
    let fontForMeasure = 'italic 500 42px system-ui, -apple-system, "Segoe UI", sans-serif';
    let lineHeight = 48;
    let measureText = `\u201C${caption}\u201D`;
    if (style === 'handwritten') {
      try {
        if (document?.fonts?.load) await document.fonts.load('500 64px "Caveat"');
      } catch (_) {}
      fontForMeasure = '500 64px "Caveat", "Comic Sans MS", cursive';
      lineHeight = 60;
      measureText = caption;
    } else if (style === 'headline') {
      fontForMeasure = 'bold 700 50px system-ui, -apple-system, "Segoe UI", sans-serif';
      lineHeight = 56;
      measureText = caption.toUpperCase();
    }
    ctx.font = fontForMeasure;
    const lines = countWrappedLines(ctx, measureText, OUT_W - PADDING * 2, 3);
    HEADER_H = Math.max(
      HEADER_H_WITH_CAPTION_BASE,
      PADDING + 180 + lines * lineHeight + 24,
    );
  }

  if (!includeHeader) HEADER_H = 0;

  // Background gradient (brand-ish purple → amber). When the header is
  // hidden we still paint the gradient so the footer band keeps its colour,
  // but the photos cover almost the entire canvas.
  const grad = ctx.createLinearGradient(0, 0, 0, OUT_H);
  grad.addColorStop(0, '#7C3AED');
  grad.addColorStop(1, '#F59E0B');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, OUT_W, OUT_H);

  if (includeHeader) {
    // Header text
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = 'bold 56px system-ui, -apple-system, "Segoe UI", sans-serif';
    wrapText(ctx, meta.title || 'Event Recap', OUT_W / 2, PADDING + 8, OUT_W - PADDING * 2, 62, 2);

    ctx.font = '400 30px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    const subtitleParts = [];
    if (meta.attendeeCount != null) {
      subtitleParts.push(`${meta.attendeeCount} attendee${meta.attendeeCount === 1 ? '' : 's'}`);
    }
    subtitleParts.push(`${photos.length} photo${photos.length === 1 ? '' : 's'}`);
    if (meta.dateLabel) subtitleParts.push(meta.dateLabel);
    ctx.fillText(subtitleParts.join(' · '), OUT_W / 2, PADDING + 140);

    // Optional user caption / quote — styled per `captionStyle` ('quote' |
    // 'handwritten' | 'headline'). Rendered under the subtitle inside the
    // gradient header. Sizes bumped so the quote reads well at Instagram's
    // typical display scale, with up to 3 lines of wrap room.
    if (caption) {
      const style = meta.captionStyle || 'quote';
      let fontSpec = 'italic 500 42px system-ui, -apple-system, "Segoe UI", sans-serif';
      let lineHeight = 48;
      let text = `\u201C${caption}\u201D`;
      if (style === 'handwritten') {
        try {
          if (document?.fonts?.load) await document.fonts.load('500 64px "Caveat"');
        } catch (_) { /* fall back silently */ }
        fontSpec = '500 64px "Caveat", "Comic Sans MS", cursive';
        lineHeight = 60;
        text = caption;
      } else if (style === 'headline') {
        fontSpec = 'bold 700 50px system-ui, -apple-system, "Segoe UI", sans-serif';
        lineHeight = 56;
        text = caption.toUpperCase();
      }
      ctx.font = fontSpec;
      ctx.fillStyle = '#ffffff';
      // Caption sits right under the subtitle (subtitle is drawn at y+140).
      wrapText(ctx, text, OUT_W / 2, PADDING + 180, OUT_W - PADDING * 2, lineHeight, 3);
    }
  }

  // Load images
  const layout = gridLayout(photos.length);
  const slice = photos.slice(0, layout.count);
  const loaded = await Promise.all(slice.map((p) => loadImage(p.url)));

  const gridX = PADDING;
  const gridY = includeHeader ? HEADER_H + PADDING : PADDING;
  const gridW = OUT_W - PADDING * 2;
  const gridH = OUT_H - (includeHeader ? HEADER_H : 0) - FOOTER_H - PADDING * 2;
  const gutter = 12;

  loaded.forEach((img, i) => {
    const cellSpec = layout.cells[i];
    if (!cellSpec) return;
    // Convert normalised cell to pixel coords, shrinking by half a gutter on
    // every edge that isn't touching the grid boundary so adjacent cells get
    // a clean gap.
    const leftGutter  = cellSpec.x > 0 ? gutter / 2 : 0;
    const rightGutter = cellSpec.x + cellSpec.w < 1 ? gutter / 2 : 0;
    const topGutter    = cellSpec.y > 0 ? gutter / 2 : 0;
    const bottomGutter = cellSpec.y + cellSpec.h < 1 ? gutter / 2 : 0;
    const x = gridX + cellSpec.x * gridW + leftGutter;
    const y = gridY + cellSpec.y * gridH + topGutter;
    const cellW = cellSpec.w * gridW - leftGutter - rightGutter;
    const cellH = cellSpec.h * gridH - topGutter - bottomGutter;

    // Rounded rect clip
    const r = 20;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + cellW, y, x + cellW, y + cellH, r);
    ctx.arcTo(x + cellW, y + cellH, x, y + cellH, r);
    ctx.arcTo(x, y + cellH, x, y, r);
    ctx.arcTo(x, y, x + cellW, y, r);
    ctx.closePath();
    ctx.clip();

    if (img) {
      // Cover-fit the image into the cell
      const ar = img.width / img.height;
      const cellAr = cellW / cellH;
      let sx = 0;
      let sy = 0;
      let sw = img.width;
      let sh = img.height;
      if (ar > cellAr) {
        // wider than cell — crop horizontally
        sw = img.height * cellAr;
        sx = (img.width - sw) / 2;
      } else {
        // taller than cell — crop vertically
        sh = img.width / cellAr;
        sy = (img.height - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, x, y, cellW, cellH);
    } else {
      // Placeholder for a failed image — subtle gradient tile.
      const g = ctx.createLinearGradient(x, y, x + cellW, y + cellH);
      g.addColorStop(0, 'rgba(255,255,255,0.25)');
      g.addColorStop(1, 'rgba(255,255,255,0.1)');
      ctx.fillStyle = g;
      ctx.fillRect(x, y, cellW, cellH);
    }
    ctx.restore();
  });

  // Footer watermark
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 32px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.fillText('expatssphere.com', OUT_W / 2, OUT_H - FOOTER_H / 2);

  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9)
  );

  return { blob, dataUrl, count: slice.length };
}

/**
 * Hand a generated collage blob to the OS share sheet (with file), falling
 * back to a plain download link if the platform doesn't support file share.
 * Always returns after the user chooses / dismisses.
 */
export async function shareOrDownloadBlob({ blob, filename, title, text }) {
  if (blob && navigator.canShare) {
    try {
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
        return { path: 'share' };
      }
    } catch (_) {
      // fall through to download
    }
  }
  return downloadBlob({ blob, filename });
}

/**
 * Save the blob directly to the user's device without going through the OS
 * share sheet. Used by "Download to Share" flows where the user explicitly
 * asked for the file so they can attach it in whichever app they want.
 */
export function downloadBlob({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return { path: 'download' };
}
